import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { CHECKIN_QUESTIONS, getCurrentWeekMonday, formatWeekLabel } from '../../utils/weeklyCheckin';
import './WeeklyCheckIn.css';

export default function WeeklyCheckIn() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [answers, setAnswers] = useState({});
  const [lastSubmission, setLastSubmission] = useState(null); // { submitted_at, answers }
  const [locked, setLocked] = useState(false);
  const [unlockDate, setUnlockDate] = useState(null);

  const weekOf = getCurrentWeekMonday();

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    setLoading(true);

    const { data: clientData } = await supabase
      .from('clients')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    if (!clientData) {
      setLoading(false);
      return;
    }
    setClient(clientData);

    // Get the most recent check-in (any week)
    const { data: recentCheckins } = await supabase
      .from('weekly_checkins')
      .select('*')
      .eq('client_id', clientData.id)
      .not('submitted_at', 'is', null)
      .order('submitted_at', { ascending: false })
      .limit(1);

    const lastCheckin = recentCheckins?.[0] || null;

    if (lastCheckin && lastCheckin.submitted_at) {
      const submittedDate = new Date(lastCheckin.submitted_at);
      const now = new Date();
      const daysSince = Math.floor((now - submittedDate) / (1000 * 60 * 60 * 24));

      if (daysSince < 7) {
        // Locked — show previous answers read-only
        setLocked(true);
        const unlock = new Date(submittedDate);
        unlock.setDate(unlock.getDate() + 7);
        setUnlockDate(unlock);

        // Restore answers for read-only display
        const restored = {};
        for (const q of CHECKIN_QUESTIONS) {
          if (lastCheckin[q.key]) restored[q.key] = lastCheckin[q.key];
        }
        setLastSubmission({
          submitted_at: lastCheckin.submitted_at,
          answers: restored,
          week_of: lastCheckin.week_of,
        });
        setLoading(false);
        return;
      }
    }

    // Not locked — check for a draft for this week
    const { data: currentWeek } = await supabase
      .from('weekly_checkins')
      .select('*')
      .eq('client_id', clientData.id)
      .eq('week_of', weekOf)
      .limit(1);

    const existing = currentWeek?.[0];
    if (existing) {
      const restored = {};
      for (const q of CHECKIN_QUESTIONS) {
        if (existing[q.key]) restored[q.key] = existing[q.key];
      }
      setAnswers(restored);

      // If already submitted this week, lock it
      if (existing.submitted_at) {
        setLocked(true);
        const unlock = new Date(existing.submitted_at);
        unlock.setDate(unlock.getDate() + 7);
        setUnlockDate(unlock);
        setLastSubmission({
          submitted_at: existing.submitted_at,
          answers: restored,
          week_of: existing.week_of,
        });
      }
    }

    setLoading(false);
  }

  function updateAnswer(key, value) {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const payload = {
        client_id: client.id,
        week_of: weekOf,
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      for (const q of CHECKIN_QUESTIONS) {
        payload[q.key] = answers[q.key] || null;
      }

      const { error: upsertError } = await supabase
        .from('weekly_checkins')
        .upsert(payload, { onConflict: 'client_id,week_of' });

      if (upsertError) throw upsertError;

      // Send email to practitioner
      try {
        await supabase.functions.invoke('send-checkin-email', {
          body: {
            clientId: client.id,
            clientName: client.name,
            practitionerId: client.practitioner_id,
            weekOf,
            answers,
          },
        });
      } catch (emailErr) {
        // Don't block submission if email fails
        console.error('Check-in email failed:', emailErr);
      }

      setSaved(true);
    } catch (err) {
      setError(err.message || 'Failed to save check-in.');
      setSaving(false);
    }
  }

  const answeredCount = CHECKIN_QUESTIONS.filter(
    (q) => answers[q.key] && answers[q.key].trim().length > 0
  ).length;

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading...
        </div>
      </div>
    );
  }

  // Just submitted — success screen
  if (saved) {
    return (
      <div className="screen container">
        <div className="wci-saved">
          <div className="wci-saved-icon">✓</div>
          <h2>Check-In Submitted!</h2>
          <p>Your weekly check-in has been sent to your practitioner.</p>
          <button className="btn btn-primary" onClick={() => navigate('/client')} style={{ marginTop: 16 }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Locked state — show confirmation + previous answers
  if (locked && lastSubmission) {
    const unlockStr = unlockDate
      ? unlockDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
      : '';

    return (
      <div className="screen container wci-screen">
        <button onClick={() => navigate('/client')} className="back-link">
          ← Home
        </button>

        <h1 className="wci-title">Weekly Check-In</h1>

        <div className="wci-locked-banner">
          <div className="wci-locked-icon">✓</div>
          <div>
            <p className="wci-locked-text">Check-in submitted!</p>
            <p className="wci-locked-date">Your next check-in opens {unlockStr}</p>
          </div>
        </div>

        <p className="wci-readonly-label">Your last submission:</p>

        <div className="wci-form">
          {CHECKIN_QUESTIONS.map((q, index) => (
            <div key={q.key} className="wci-question">
              <label className="wci-label">
                <span className="wci-number">{index + 1}.</span>
                {q.question}
              </label>
              <div className="wci-readonly-answer">
                {lastSubmission.answers[q.key] || '—'}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Open state — editable form
  return (
    <div className="screen container wci-screen">
      <button onClick={() => navigate('/client')} className="back-link">
        ← Home
      </button>

      <h1 className="wci-title">Weekly Check-In</h1>
      <p className="wci-week">Week of {formatWeekLabel(weekOf)}</p>
      <p className="wci-progress">
        {answeredCount} of {CHECKIN_QUESTIONS.length} questions answered
      </p>

      <form className="wci-form" onSubmit={handleSubmit}>
        {error && <div className="form-error">{error}</div>}

        {CHECKIN_QUESTIONS.map((q, index) => (
          <div key={q.key} className="wci-question">
            <label className="wci-label">
              <span className="wci-number">{index + 1}.</span>
              {q.question}
            </label>
            <textarea
              className="form-input wci-textarea"
              rows={3}
              placeholder={q.placeholder}
              value={answers[q.key] || ''}
              onChange={(e) => updateAnswer(q.key, e.target.value)}
            />
          </div>
        ))}

        <button type="submit" className="btn btn-primary wci-submit" disabled={saving}>
          {saving ? 'Submitting...' : 'Submit Check-In'}
        </button>
      </form>
    </div>
  );
}
